<template>
    <v-app>
        <v-app-bar app flat height="76" color="transparent" class="sophae-appbar">
            <v-container class="d-flex align-center sophae-appbar-inner">
                <!-- Left: logo / brand -->
                <div class="d-flex align-center brand-cluster" @click="$router.push({ name: 'home' })">
                    <img alt="Sophae" width="34" src="../src/assets/sophae_logo2.png" class="brand-mark mr-3">
                    <div class="d-flex flex-column">
                        <span class="brand-wordmark">Sophae</span>
                        <span class="brand-subline d-none d-md-block">Compounding Decision Intelligence</span>
                    </div>
                </div>

                <!-- Center: nav links (hidden on xs) -->
                <div class="d-none d-md-flex align-center sophae-nav-links">
                    <v-btn text small class="sophae-nav-link" @click="$router.push({ name: 'vision' })">
                        Vision
                    </v-btn>
                    <v-btn text small class="sophae-nav-link" @click="$router.push({ name: 'careers' })">
                        Careers
                    </v-btn>
                    <v-btn text small class="sophae-nav-link" @click="$router.push({ name: 'faq' })">
                        FAQ
                    </v-btn>
                </div>

                <!-- Right: actions -->
                <div class="d-flex align-center">
                    <v-btn small class="sophae-cta-btn" @click="openDemoDialog">
                        Request a demo
                        <v-icon right size="16">mdi-arrow-right</v-icon>
                    </v-btn>
                </div>
            </v-container>
        </v-app-bar>

        <router-view />

        <!-- Footer -->
        <footer class="sophae-footer">
            <v-container class="sophae-footer-inner">
                <div class="d-flex flex-wrap align-center justify-space-between">
                    <div class="d-flex align-center">
                        <img alt="Sophae" width="28" src="../src/assets/sophae_logo2.png" class="mr-3">
                        <div>
                            <div class="footer-wordmark">Sophae</div>
                            <div class="footer-tagline">Portfolio intelligence for private equity.</div>
                        </div>
                    </div>
                    <div class="footer-links">
                        <a @click="$router.push({ name: 'vision' })">Vision</a>
                        <a @click="$router.push({ name: 'careers' })">Careers</a>
                        <a @click="$router.push({ name: 'faq' })">FAQ</a>
                        <a @click="onContact">Contact</a>
                        <a @click="openDemoDialog">Request a demo</a>
                    </div>
                </div>
                <div class="footer-base">
                    <span>© {{ year }} Sophae. All rights reserved.</span>
                    <span class="footer-base-dot">·</span>
                    <span>Compounding Decision Intelligence.</span>
                </div>
            </v-container>
        </footer>

        <!-- Demo request dialog -->
        <v-dialog v-model="demoDialog" max-width="640px" scrollable>
            <v-card class="demo-card">
                <v-card-title class="d-flex justify-space-between align-center pb-2">
                    <div>
                        <div class="demo-eyebrow">Request a demo</div>
                        <div class="demo-title">Explore Sophae for your portfolio.</div>
                    </div>
                    <v-btn icon @click="demoDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pt-4">
                    <p class="demo-subtitle">
                        Tell us about your firm and the portfolio company you have in mind. We will be in touch to discuss a focused walkthrough.
                    </p>

                    <v-form ref="demoForm" v-model="demoFormValid" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="demo.firstName" label="First name" outlined dense dark
                                    color="amber lighten-2" :rules="[v => !!v || 'Required']"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="demo.lastName" label="Last name" outlined dense dark
                                    color="amber lighten-2" :rules="[v => !!v || 'Required']"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="demo.email" label="Work email" outlined dense dark
                                    color="amber lighten-2" :rules="emailRules"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="demo.company" label="Company" outlined dense dark
                                    color="amber lighten-2" :rules="[v => !!v || 'Required']"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="demo.role" :items="roleOptions" label="Your role" outlined dense
                                    dark color="amber lighten-2"></v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="demo.size" :items="sizeOptions" label="Organization size" outlined
                                    dense dark color="amber lighten-2"></v-select>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea v-model="demo.notes"
                                    label="What would you like to explore about your portfolio?" outlined dense dark
                                    color="amber lighten-2" rows="3"></v-textarea>
                            </v-col>
                        </v-row>

                        <div class="d-flex flex-wrap align-center justify-space-between mt-2">
                            <div class="demo-trust">
                                <v-icon small class="mr-1">mdi-shield-check</v-icon>
                                Your information is treated as confidential.
                            </div>
                            <v-btn class="sophae-cta-btn ml-auto" :disabled="!demoFormValid" @click="submitDemo">
                                Send request
                                <v-icon right size="16">mdi-arrow-right</v-icon>
                            </v-btn>
                        </div>
                    </v-form>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Waitlist dialog -->
        <v-dialog v-model="waitlistDialog" max-width="560px" scrollable>
            <v-card class="demo-card">
                <v-card-title class="d-flex justify-space-between align-center pb-2">
                    <div>
                        <div class="demo-eyebrow">Join the waitlist</div>
                        <div class="demo-title">Be among the first organizations on the platform.</div>
                    </div>
                    <v-btn icon @click="waitlistDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pt-4">
                    <p class="demo-subtitle">
                        The earliest users compound the longest. We'll reach out as access opens. No marketing spam,
                        no sales pressure.
                    </p>

                    <v-form ref="waitForm" v-model="waitFormValid" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="wait.firstName" label="First name" outlined dense dark
                                    color="amber lighten-2" :rules="[v => !!v || 'Required']"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="wait.lastName" label="Last name" outlined dense dark
                                    color="amber lighten-2" :rules="[v => !!v || 'Required']"></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="wait.email" label="Work email" outlined dense dark
                                    color="amber lighten-2" :rules="emailRules"></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="wait.company" label="Company (optional)" outlined dense dark
                                    color="amber lighten-2"></v-text-field>
                            </v-col>
                        </v-row>

                        <div class="d-flex flex-wrap align-center justify-space-between mt-2">
                            <div class="demo-trust">
                                <v-icon small class="mr-1">mdi-shield-check</v-icon>
                                We'll only email you about Sophae.
                            </div>
                            <v-btn class="sophae-cta-btn ml-auto" :disabled="!waitFormValid" @click="submitWaitlist">
                                Join waitlist
                                <v-icon right size="16">mdi-arrow-right</v-icon>
                            </v-btn>
                        </div>
                    </v-form>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snack.open" :timeout="3200" top right color="amber darken-3">
            <span class="black--text font-weight-medium">
                {{ snack.message }}
            </span>
        </v-snackbar>
    </v-app>
</template>

<script>
import './assets/editorial-pages.css'
export default {
    name: 'App',
    data() {
        return {
            demoDialog: false,
            demoFormValid: true,
            waitlistDialog: false,
            waitFormValid: true,
            year: new Date().getFullYear(),
            snack: {
                open: false,
                message: '',
            },
            demo: {
                firstName: '',
                lastName: '',
                email: '',
                company: '',
                role: '',
                size: '',
                notes: '',
            },
            wait: {
                firstName: '',
                lastName: '',
                email: '',
                company: '',
            },
            roleOptions: [
                'CEO / Founder',
                'CFO',
                'COO',
                'Chief Risk Officer',
                'GP / Operating Partner (PE/VC)',
                'Board / Audit Committee',
                'Strategy / M&A',
                'General Counsel / Compliance',
                'Other',
            ],
            sizeOptions: [
                'Seed / Series A',
                'Series B–D',
                'Growth / Pre-IPO',
                'Public Company',
                'Private Equity Fund',
                'Venture Fund',
                'Family Office',
            ],
            emailRules: [
                v => !!v || 'Required',
                v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || 'Enter a valid email',
            ],
        }
    },
    mounted() {
        this.$root.$on('open-demo', this.openDemoDialog)
        this.$root.$on('open-waitlist', this.openWaitlistDialog)
    },
    beforeDestroy() {
        this.$root.$off('open-demo', this.openDemoDialog)
        this.$root.$off('open-waitlist', this.openWaitlistDialog)
    },
    methods: {
        openDemoDialog() {
            this.demoDialog = true
        },
        openWaitlistDialog() {
            this.waitlistDialog = true
        },
        // Recipient is base64-encoded so the literal address doesn't appear
        // in the static HTML source. Decoded at click time only.
        _recipient() {
            return atob('Y29ucmFkLm1pY2hhZWwubW9zc0BnbWFpbC5jb20=')
        },
        onContact() {
            window.location.href =
                `mailto:${this._recipient()}?subject=Sophae%20%E2%80%93%20Inquiry`
        },
        submitDemo() {
            if (this.$refs.demoForm && !this.$refs.demoForm.validate()) return

            const subject = encodeURIComponent(`Sophae demo request: ${this.demo.company || this.demo.lastName}`)
            const body = encodeURIComponent(
                `Name: ${this.demo.firstName} ${this.demo.lastName}\n` +
                `Email: ${this.demo.email}\n` +
                `Company: ${this.demo.company}\n` +
                `Role: ${this.demo.role}\n` +
                `Size: ${this.demo.size}\n\n` +
                `Notes:\n${this.demo.notes}\n`
            )
            window.location.href = `mailto:${this._recipient()}?subject=${subject}&body=${body}`

            this.snack.message = 'Thanks. Your request is on its way.'
            this.snack.open = true
            this.demoDialog = false
        },
        submitWaitlist() {
            if (this.$refs.waitForm && !this.$refs.waitForm.validate()) return

            const subject = encodeURIComponent(`Sophae waitlist: ${this.wait.firstName} ${this.wait.lastName}`)
            const body = encodeURIComponent(
                `Name: ${this.wait.firstName} ${this.wait.lastName}\n` +
                `Email: ${this.wait.email}\n` +
                `Company: ${this.wait.company}\n`
            )
            window.location.href = `mailto:${this._recipient()}?subject=${subject}&body=${body}`

            this.snack.message = "You're on the list. We'll be in touch."
            this.snack.open = true
            this.waitlistDialog = false
        },
    },
}
</script>

<style>
/* GLOBAL: base typography and selection colors */
html,
body,
#app {
    background: #04060f;
    color: #e6e8ef;
}

.v-application {
    font-family: 'Inter', 'Roboto', system-ui, -apple-system, BlinkMacSystemFont, sans-serif !important;
    background: #04060f !important;
    letter-spacing: -0.005em;
}

::selection {
    background: rgba(245, 158, 11, 0.35);
    color: #fff;
}

.sophae-grad {
    background: linear-gradient(92deg, #f8c47a 0%, #fbbf24 40%, #38bdf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.sophae-grad-cool {
    background: linear-gradient(92deg, #e7eaf5 0%, #c8d3ff 50%, #7dd3fc 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.display-xl {
    font-size: clamp(2.6rem, 5.2vw, 4.6rem);
    font-weight: 300;
    line-height: 1.04;
    letter-spacing: -0.025em;
}

.display-lg {
    font-size: clamp(2rem, 3.6vw, 3.2rem);
    font-weight: 300;
    line-height: 1.08;
    letter-spacing: -0.02em;
}

.eyebrow {
    font-size: 0.72rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: #f6c98a;
}

.section-lede {
    font-size: 1.08rem;
    color: #a4abc0;
    line-height: 1.65;
    max-width: 640px;
}

/* A quieter shell lets the sculptural hero lead. */
.sophae-appbar { background: rgba(4,6,11,.88) !important; border-bottom: 1px solid rgba(255,255,255,.06); }
.brand-subline { display:none !important; }
.sophae-cta-btn { background:#d2e3ee !important; color:#101820 !important; box-shadow:none !important; border-radius:4px !important; }
.sophae-footer { background:#04060b; }
</style>

<style scoped>
.sophae-appbar {
    background: linear-gradient(180deg, rgba(4, 6, 15, 0.92) 0%, rgba(4, 6, 15, 0.65) 70%, rgba(4, 6, 15, 0)) !important;
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.sophae-appbar-inner {
    max-width: 1240px;
}

.brand-cluster {
    cursor: pointer;
    user-select: none;
}

.brand-mark {
    filter: drop-shadow(0 0 8px rgba(245, 158, 11, 0.25));
}

.brand-wordmark {
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    font-size: 0.94rem;
    color: #f5f7fb;
    line-height: 1;
}

.brand-subline {
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #9aa3bd;
    margin-top: 3px;
    line-height: 1;
}

.sophae-nav-links {
    flex: 1;
    justify-content: center;
}

.sophae-nav-link {
    font-size: 0.82rem !important;
    text-transform: none !important;
    letter-spacing: 0.01em;
    color: #cdd2e1 !important;
    padding: 0 14px !important;
    font-weight: 400 !important;
}

.sophae-nav-link:hover {
    color: #fff !important;
}

.sophae-secondary-btn {
    text-transform: none !important;
    font-size: 0.82rem !important;
    color: #cdd2e1 !important;
    font-weight: 400 !important;
}

.sophae-secondary-btn:hover {
    color: #f6c98a !important;
}

.sophae-cta-btn {
    text-transform: none !important;
    font-size: 0.82rem !important;
    font-weight: 500 !important;
    border-radius: 10px !important;
    padding: 0 18px !important;
    height: 38px !important;
    background: linear-gradient(135deg, #f6b76a 0%, #f59e0b 50%, #ea8a17 100%) !important;
    color: #0a0d1c !important;
    box-shadow: 0 8px 24px rgba(245, 158, 11, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.25) !important;
    transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}

.sophae-cta-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 12px 32px rgba(245, 158, 11, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

.demo-card {
    background: linear-gradient(180deg, #0c1024 0%, #060914 100%) !important;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 18px !important;
    color: #e6e8ef;
}

.demo-eyebrow {
    font-size: 0.7rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f6c98a;
    margin-bottom: 4px;
}

.demo-title {
    font-size: 1.35rem;
    font-weight: 500;
    color: #f5f7fb;
}

.demo-subtitle {
    color: #a4abc0;
    font-size: 0.92rem;
    margin-bottom: 16px;
}

.demo-trust {
    font-size: 0.78rem;
    color: #8a92ad;
    display: inline-flex;
    align-items: center;
}

.sophae-footer {
    background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.5));
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    padding: 56px 0 32px;
    margin-top: 0;
}

.sophae-footer-inner {
    max-width: 1240px;
}

.footer-wordmark {
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #f5f7fb;
    font-size: 0.92rem;
    line-height: 1;
}

.footer-tagline {
    color: #8a92ad;
    font-size: 0.8rem;
    margin-top: 4px;
}

.footer-links {
    display: flex;
    flex-wrap: wrap;
    gap: 28px;
    margin-top: 12px;
}

.footer-links a {
    color: #cdd2e1;
    font-size: 0.85rem;
    cursor: pointer;
    transition: color 0.2s ease;
}

.footer-links a:hover {
    color: #f6c98a;
}

.footer-base {
    margin-top: 32px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.04);
    color: #6e7691;
    font-size: 0.78rem;
}

.footer-base-dot {
    margin: 0 10px;
    color: #45506e;
}

@media (max-width: 960px) {
    .sophae-appbar-inner {
        max-width: 100%;
    }

    .footer-links {
        gap: 18px;
        margin-top: 20px;
    }
}
</style>

<style>
.v-application .sophae-appbar { background:rgba(4,6,11,.94)!important; border-bottom:1px solid #ffffff0c; }
.v-application .brand-subline { display:none!important; }
.v-application .sophae-cta-btn { background:#d2e3ee!important; color:#101820!important; box-shadow:none!important; border-radius:4px!important; }
.v-application .sophae-footer { background:#04060b; }
</style>
